/**
 * previewIframe — shared DOM helpers for locating an open HTML-preview iframe
 * in the workspace editor, used by the client-executed inspection tools
 * (previewScreenshotClient / previewEvalClient).
 *
 * The tab wrapper carries `data-testid="tab-viewer" data-file-id=<id>`
 * (TabContent.tsx); the preview iframes are titled "HTML Preview[ A|B]" and, in
 * session mode, double-buffered (two iframes — the visible one is authoritative).
 *
 * Pure DOM — no React, no network, unit-testable. Kept dependency-free (the
 * workspace marker comes from core-ui's tiny pure `surfaceScope` subpath) so
 * both client handlers import ONE copy instead of duplicating the selectors
 * (which drift the moment TabContent's data-attrs change).
 *
 * Every open workspace stays mounted, so a preview is looked up WITHIN the
 * workspace a call is bound to (#352) — first in DOM order across all of them
 * was another session's app.
 */
import { workspaceScopeRoots, workspaceScopeLoaded, cssEscape, isShownWithin } from '@papan/core-ui/ui/surfaceScope'
import { readClientToolTarget, type ClientToolTarget } from '../core/client-tool-target.js'
import { frameMessageTarget, frameMessageFrom } from '@papan/collab/ui/app-origin-frame'


/** Normalize a model-supplied workspace path to an absolute `/papan/...` vpath. */
export function toAbsoluteVfsPath(raw: string): string {
  const stripped = raw.trim().replace(/^\/+/, '').replace(/^papan\//i, '')
  return `/papan/${stripped}`
}

/** Is an iframe visually hidden? Best-effort — jsdom lacks layout, so treat a
 *  read failure as visible (rank it first). */
function isHidden(f: HTMLIFrameElement): boolean {
  try {
    return getComputedStyle(f).visibility === 'hidden'
  } catch {
    return false
  }
}

/** All HTML-preview iframes for `fileId`, VISIBLE-FIRST. Session mode
 *  double-buffers two iframes ("HTML Preview A"/"B"); during a reload swap BOTH
 *  can be present — one showing current content (bridge live), one mid-load or a
 *  blank src="" spare (bridge dead). The DOM can't tell them apart reliably
 *  (visibility flips at swap time, before the incoming buffer finishes loading),
 *  so callers ping each and use whichever answers — this only orders them so the
 *  authoritative (visible) buffer is preferred when more than one is live. */
export function findPreviewFrames(fileId: string, workspaceId?: string | null): HTMLIFrameElement[] {
  if (typeof document === 'undefined') return []
  const sel = `[data-testid="tab-viewer"][data-file-id="${cssEscape(fileId)}"]`
  // The call's own workspace first — the same file can be open in two, and
  // its copy there is the one the session is working with. Any copy of the
  // SAME file is still that file, so elsewhere is a fallback, not a refusal.
  const scoped = workspaceId ? workspaceScopeRoots(workspaceId).map(r => r.querySelector(sel)).find(Boolean) : null
  const container = scoped ?? document.querySelector(sel)
  if (!container) return []
  const frames = (Array.from(container.querySelectorAll('iframe')) as HTMLIFrameElement[])
    .filter((f) => (f.getAttribute('title') || '').startsWith('HTML Preview') && !!f.contentWindow)
  return frames.sort((a, b) => Number(isHidden(a)) - Number(isHidden(b)))
}

/** Find an open HTML-preview iframe for `fileId`. Synchronous, DOM-only — it
 *  can't tell a mid-swap / blank buffer from a live one, so prefer
 *  {@link pickReadyPreviewFrame} for anything that then posts to the frame. Kept
 *  for callers that only need a "does a preview exist" check. */
export function findPreviewIframe(fileId: string): HTMLIFrameElement | null {
  return findPreviewFrames(fileId)[0] ?? null
}

/** Post `papan:ping` to a frame and resolve true iff its bridge pongs within
 *  `timeoutMs`. A pong means the injected bridge is LOADED and listening
 *  (independent of whether eval is enabled) — i.e. the frame can answer an
 *  eval/snapshot request. Source-gated: only the frame we pinged may answer. */
export function pingPreviewFrame(iframe: HTMLIFrameElement, timeoutMs: number): Promise<boolean> {
  const cw = iframe.contentWindow
  if (!cw || typeof window === 'undefined') return Promise.resolve(false)
  // #1408 R6: an app-origin frame is pinged at, and heard from, its exact origin only.
  const target = frameMessageTarget(iframe)
  return new Promise<boolean>((resolve) => {
    const reqId = `agentping_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const onMsg = (e: MessageEvent) => {
      if (!frameMessageFrom(e, cw, target)) return
      const d = e.data
      if (d && d.type === 'papan:pong' && d.reqId === reqId) {
        cleanup()
        resolve(true)
      }
    }
    const timer = setTimeout(() => { cleanup(); resolve(false) }, timeoutMs)
    function cleanup() {
      clearTimeout(timer)
      window.removeEventListener('message', onMsg)
    }
    window.addEventListener('message', onMsg)
    try {
      cw.postMessage({ type: 'papan:ping', reqId }, target)
    } catch {
      cleanup()
      resolve(false)
    }
  })
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/** Result of {@link pickReadyPreviewFrame}. `everSawCandidate` lets the caller
 *  tell a preview that is RELOADING (a buffer was present but never became
 *  ready — retryable) from one that is genuinely NOT OPEN (no iframe ever
 *  appeared). */
export interface ReadyPreviewFrame {
  frame: HTMLIFrameElement | null
  everSawCandidate: boolean
}

/** Wait out a double-buffer reload swap and return a frame whose bridge is live.
 *
 *  The bug this fixes: posting an eval/snapshot straight at `findPreviewIframe`
 *  hits whichever buffer the DOM says is visible — which, mid-swap, is often the
 *  incoming buffer whose bridge hasn't loaded yet, or a blank spare. The request
 *  then dead-waits its full timeout and the tool falsely reports "not open".
 *
 *  Instead we ping every candidate (cheap, no side effects) and return the first
 *  that pongs, retrying across the swap window so a buffer that is still loading
 *  gets a chance to come up. Returns `{ frame: null }` only after the whole
 *  window elapses with nothing answering. */
export async function pickReadyPreviewFrame(
  fileId: string,
  opts: { windowMs?: number; pingMs?: number; gapMs?: number } = {},
): Promise<ReadyPreviewFrame> {
  const windowMs = opts.windowMs ?? 12_000
  const pingMs = opts.pingMs ?? 700
  const gapMs = opts.gapMs ?? 200
  const deadline = Date.now() + windowMs
  let everSawCandidate = false
  for (;;) {
    const frames = findPreviewFrames(fileId)
    if (frames.length > 0) everSawCandidate = true
    if (frames.length > 0) {
      // Ping all candidates concurrently; take the first (visible-first) that
      // answers, so a live-and-visible buffer wins over a live-but-hidden one.
      const answered = await Promise.all(frames.map((f) => pingPreviewFrame(f, pingMs)))
      const idx = answered.findIndex(Boolean)
      if (idx >= 0) return { frame: frames[idx], everSawCandidate }
    }
    if (Date.now() >= deadline) return { frame: null, everSawCandidate }
    await sleep(gapMs)
  }
}

/** Resolve a live, bridge-ready preview frame from an ordered list of candidate
 *  file ids (e.g. a `.miniapp` folder id then its entry-HTML id).
 *
 *  ALL candidates are pinged CONCURRENTLY each round under ONE shared deadline —
 *  never one full window per candidate. That matters when the primary id's buffer
 *  is mounted but DEAD (mid-swap / blank spare) while a fallback id has a live
 *  one: a per-candidate serial wait would burn the whole window on the dead
 *  primary before ever pinging the fallback. Candidate order still breaks ties —
 *  a live primary buffer wins over a live fallback in the same round.
 *
 *  Returns the first ready frame, or `{ frame: null }` with `sawCandidate`
 *  distinguishing RELOADING (true — a buffer was mounted but never answered,
 *  retryable) from NOT OPEN (false). When NO candidate has a mounted iframe it's
 *  almost always genuinely-not-open (the double buffer keeps ≥1 iframe mounted
 *  through a swap), so it only waits a short grace for a first-mount blip. Shared
 *  by preview_eval and preview_screenshot so they can't disagree about which
 *  buffer is live. */
export async function resolveReadyPreviewFrame(
  candidates: string[],
  opts: { windowMs?: number; pingMs?: number; gapMs?: number; workspaceId?: string | null } = {},
): Promise<{ frame: HTMLIFrameElement | null; sawCandidate: boolean }> {
  const pingMs = opts.pingMs ?? 700
  const gapMs = opts.gapMs ?? 200
  const anyPresent = candidates.some((id) => findPreviewFrames(id, opts.workspaceId).length > 0)
  const windowMs = opts.windowMs ?? (anyPresent ? 12_000 : 800)
  const deadline = Date.now() + windowMs
  let sawCandidate = anyPresent
  for (;;) {
    // Gather every candidate's frames THIS round (a buffer can mount/swap mid
    // wait), primary-id-first so its live buffer wins ties.
    const frames = candidates.flatMap((id) => findPreviewFrames(id, opts.workspaceId))
    if (frames.length > 0) {
      sawCandidate = true
      const answered = await Promise.all(frames.map((f) => pingPreviewFrame(f, pingMs)))
      const idx = answered.findIndex(Boolean)
      if (idx >= 0) return { frame: frames[idx], sawCandidate: true }
    }
    if (Date.now() >= deadline) return { frame: null, sawCandidate }
    await sleep(gapMs)
  }
}

/**
 * #356: is an HTML-preview reload currently IN FLIGHT?
 *
 * HtmlViewer double-buffers a reload (load the spare buffer, swap on its
 * `onload`); during that window the bridge can be momentarily unpingable and a
 * client-executed inspection tool (preview_eval / preview_screenshot) dispatched
 * right then fast-fails "the app preview is not open". The tools declare a
 * `ready()` gate keyed on this so the LIVE dispatch DEFERS (retries via the
 * `/pending` poll) until the reload settles, instead of erroring — the fix for
 * #356 (argsFrom:'worker' + ready()).
 *
 * The signal is a `window` epoch-ms deadline, NOT an import: HtmlViewer lives in
 * `@papan/collab`, which `@papan/agent` depends on, so a shared symbol would
 * close a cycle. HtmlViewer writes `__papanPreviewReloadingUntil` at reload
 * start (Date.now()+bound) and zeroes it on swap `onload`. The deadline is a
 * SAFETY BOUND: if a reload never fires `onload`, the flag self-expires so the
 * tools can never be deferred indefinitely (they fall through to the handler's
 * own 12s ping window, which is the backstop). Same window-contract style as the
 * `papan:*` postMessage protocol these files already share.
 *
 * CAVEAT — the flag is a SINGLE global, not per-preview. With keep-alive decks,
 * several previews can be mounted at once, so while preview A is reloading a tool
 * call targeting the (fine) preview B also defers until A settles, and if A and B
 * reload overlapping, A's `onload` clears the flag while B is still swapping. Both
 * are accepted: the defer is a brief, self-bounded window and the typical agent
 * flow inspects one preview at a time. Making it precise means ref-counting
 * in-flight reloads or keying the deadline by fileId — deferred until an overlap
 * case actually bites.
 */
export const PREVIEW_RELOADING_KEY = '__papanPreviewReloadingUntil'
export function isPreviewReloading(): boolean {
  if (typeof window === 'undefined') return false
  const until = (window as unknown as Record<string, unknown>)[PREVIEW_RELOADING_KEY]
  return typeof until === 'number' && Date.now() < until
}

/** The preview a call with no `path` means — the `data-file-id` of a tab
 *  viewer that owns an HTML-preview iframe.
 *
 *  Bound (#352): within the call's workspace only — the chip's file when it is
 *  a preview open there, else that workspace's SHOWN tab, whether or not the
 *  workspace itself is on screen. Never another workspace's preview: first in
 *  DOM order across every mounted workspace was another session's app, and
 *  `preview_eval` runs code in it. With no target (a worker that stamps none),
 *  the old first-visible-in-the-page rule. */
export function findActivePreviewFileId(target?: ClientToolTarget | null): string | null {
  if (typeof document === 'undefined') return null
  if (target) {
    // The workspace can render in more than one place (its deck, chat-home's
    // side editor) — every one of its stacks counts, no other workspace does.
    const roots = workspaceScopeRoots(target.workspaceId)
    const hasPreview = (c: Element) => Array.from(c.querySelectorAll('iframe')).some((f) => (f.getAttribute('title') || '').startsWith('HTML Preview'))
    const viewers = roots.flatMap((root) => Array.from(root.querySelectorAll('[data-testid="tab-viewer"][data-file-id]')).map((c) => ({ c, root })))
    const chip = target.fileId ? viewers.find(({ c }) => c.getAttribute('data-file-id') === target.fileId && hasPreview(c)) : undefined
    const pick = chip ?? viewers.find(({ c, root }) => isShownWithin(c, root) && hasPreview(c))
    return pick?.c.getAttribute('data-file-id') ?? null
  }
  for (const container of Array.from(document.querySelectorAll('[data-testid="tab-viewer"][data-file-id]'))) {
    const id = container.getAttribute('data-file-id')
    if (!id) continue
    const frames = Array.from(container.querySelectorAll('iframe')).filter((f) =>
      (f.getAttribute('title') || '').startsWith('HTML Preview'),
    ) as HTMLIFrameElement[]
    if (frames.some((f) => getComputedStyle(f).visibility !== 'hidden')) return id
  }
  return null
}

/** Is the call's own preview still MOUNTING — so the right answer is "wait",
 *  not "no preview open"? Mirrors `dbEditorStillMounting`: a reload restores a
 *  workspace's tabs a round trip after its config, and a page tab's iframe
 *  after that.
 *   • no stack marked for the workspace (or no target): nothing will mount,
 *     answer at once with an accurate error;
 *   • a stack whose tabs are not restored yet: wait;
 *   • a candidate page tab with no preview iframe yet: wait — the chip's file,
 *     else the SHOWN tab of each stack when it is a family that renders one.
 *  Some such tabs NEVER get an iframe until the user acts (an uninstalled
 *  PDApp, a Settings face, a triggers-only app, a page awaiting preview
 *  consent, an over-ceiling inline page), so "mounting" is only a guess: the
 *  callers' `maxDeferMs` (20 s) bounds the wait and then the call answers. */
export function previewStillMounting(target: ClientToolTarget | null): boolean {
  if (!target) return false
  const roots = workspaceScopeRoots(target.workspaceId)
  if (roots.length === 0) return false
  if (roots.some(r => !workspaceScopeLoaded(r))) return true
  const hasFrame = (tab: Element) => !!tab.querySelector('iframe[title^="HTML Preview"]')
  // Only the families that render a preview iframe can be "still mounting";
  // a chip on any other tab (text, csv, a database…) never gets one — wait
  // for it and the call would hang to the worker's ceiling.
  const rendersPreview = (tab: Element) => {
    const family = tab.getAttribute('data-viewer-family')
    return family === 'html-page' || family === 'app'
  }
  return roots.some(root => {
    if (target.fileId) {
      const tab = root.querySelector(`[data-testid="tab-viewer"][data-file-id="${cssEscape(target.fileId)}"]`)
      return !!tab && rendersPreview(tab) && !hasFrame(tab)
    }
    return Array.from(root.querySelectorAll('[data-testid="tab-viewer"]')).some(tab => {
      return rendersPreview(tab) && isShownWithin(tab, root) && !hasFrame(tab)
    })
  })
}

/**
 * #352 I1 — can THIS page answer a preview call (preview_eval / preview_screenshot)?
 * Asks exactly what the handler needs, so a tab with no preview never claims the call
 * first and answers "preview not open":
 *  - no `path`: the call's workspace has an active preview (`findActivePreviewFileId`);
 *  - a `path`: at least one preview iframe exists in that workspace (the handler then
 *    resolves the path among them) — anywhere on the page with no target (an old
 *    worker), as the handler then searches.
 */
export function previewCallCanResolve(args: Record<string, unknown>): boolean {
  if (typeof document === 'undefined') return false
  const target = readClientToolTarget(args)
  const path = typeof args.path === 'string' ? args.path.trim() : ''
  if (!path) return findActivePreviewFileId(target) !== null
  const roots: ParentNode[] = target ? workspaceScopeRoots(target.workspaceId) : [document]
  return roots.some((r) => !!r.querySelector('iframe[title^="HTML Preview"]'))
}
